"use client";

import { useLocale } from "@/lib/context/LocaleProvider";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "@/components/ui/icons";

type PaginationProps = {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  className?: string;
};

/** Prev / page-of / Next control for paginated lists. */
export function Pagination({
  page,
  totalPages,
  onPageChange,
  disabled = false,
  className,
}: PaginationProps) {
  const { t } = useLocale();

  if (totalPages <= 1) return null;

  const label = t.paginationPageOf
    .replace("{page}", String(page))
    .replace("{totalPages}", String(totalPages));

  const btn =
    "inline-flex h-9 items-center gap-1 rounded-full border border-[rgba(26,24,22,0.14)] bg-white px-3.5 text-[12.5px] font-semibold text-ink-3 transition hover:bg-[rgba(26,24,22,0.04)] disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <nav
      className={cn(
        "mt-5 flex items-center justify-between gap-3",
        className,
      )}
      aria-label={t.paginationAria}
    >
      <button
        type="button"
        onClick={() => onPageChange(page - 1)}
        disabled={disabled || page <= 1}
        className={btn}
      >
        <ChevronLeft size={15} />
        <span>{t.paginationPrevious}</span>
      </button>
      <p className="text-[12.5px] font-semibold text-muted">{label}</p>
      <button
        type="button"
        onClick={() => onPageChange(page + 1)}
        disabled={disabled || page >= totalPages}
        className={btn}
      >
        <span>{t.paginationNext}</span>
        <ChevronRight size={15} />
      </button>
    </nav>
  );
}
